/**
 * Validates if the user needs application permissions and if she/he needs to select an account.
 */
class SuccessContainer {
  /**
   * @param {ngLocation}        $location         To redirect the user to the error page if heshe
   *                                              doesn't meet the requirements.
   * @param {ngTimeout}         $timeout          To defer the redirections.
   * @param {AppErrorHandler}   appErrorHandler   To log the error in the case an application
   *                                              permissions request fails.
   * @param {Auth}              auth              To validate whether the user has or not
   *                                              permissions for an application.
   * @param {AuthRoutesManager} authRoutesManager To get the route to login screen.
   * @param {SSOClient}         ssoClient         To send the information to the SSO client if the
   *                                              validations are successfull.
   * @param {SSOErrors}         ssoErrors         To generate an error if the user doesn't have
   *                                              permissions for an application.
   * @param {SSOState}          ssoState          To get the SSO client parameters.
   * @param {SSOStorage}        ssoStorage        To save the user email address for future logins.
   */
  constructor(
    $location,
    $timeout,
    appErrorHandler,
    auth,
    authRoutesManager,
    ssoClient,
    ssoErrors,
    ssoState,
    ssoStorage,
  ) {
    'ngInject';

    /**
     * A local reference for the `$location` service.
     * @type {ngLocation}
     */
    this.$location = $location;
    /**
     * A local reference for the `$timeout` service.
     * @type {ngTimeout}
     */
    this.$timeout = $timeout;
    /**
     * A local reference for the `appErrorHandler` service.
     * @type {AppErrorHandler}
     */
    this.appErrorHandler = appErrorHandler;
    /**
     * A local reference for the `auth` service.
     * @type {Auth}
     */
    this.auth = auth;
    /**
     * A local reference for the `authRoutesManager` service.
     * @type {AuthRoutesMan}
     */
    this.authRoutesManager = authRoutesManager;
    /**
     * A local reference for the `ssoClient` service.
     * @type {SSOClient}
     */
    this.ssoClient = ssoClient;
    /**
     * A local reference for the `ssoErrors` service.
     * @type {SSOErrors}
     */
    this.ssoErrors = ssoErrors;
    /**
     * A local reference for the `ssoState` service.
     * @type {SSOState}
     */
    this.ssoState = ssoState;
    /**
     * A local reference for the `ssoStorage` service.
     * @type {SSOStorage}
     */
    this.ssoStorage = ssoStorage;
    /**
     * This will contain the SSO client parameters once the container is mounted.
     * @type {SSOSTateSettings}
     */
    this.state = {};
    /**
     * Whether or not the user needs to have at least one permission on a selected application in
     * order to be authenticated. Its real value will be set after getting the state (when the
     * container gets mounted) and reading its settings.
     * @type {boolean}
     */
    this.needsToValidateApp = false;
    /**
     * Whether or not the user has at least one permission for a selected application. Its real
     * value will be set after the container gets mounted and the user permissions for the
     * application are loaded.
     * @type {boolean}
     */
    this.validApp = false;
    /**
     * Whether or not the container is processing a request.
     * @type {boolean}
     */
    this.loading = false;
    /**
     * This will hold the user information for the token. The container will send the user ID
     * to the SSO Client service so it can be added to the cookie.
     * @type {?Object}
     * @access protected
     * @ignore
     */
    this._user = null;
    /**
     * This will hold the user session ID the client will write on the cookie and eventually the
     * session, so it can keep track if something changed in order to refresh the saved
     * information.
     * @type {String}
     * @access protected
     * @ignore
     */
    this._sessionId = '';
  }
  /**
   * When the container gets mounted, it validates the existence of a cookie and a state before
   * calling the method that validates their properties.
   */
  $onInit() {
    if (this.token) {
      // Get the SSO client parameters.
      this.state = this.ssoState.getState();
      // If it has a state object...
      if (this.state) {
        // ...move to the function that retrieves the user and validates it.
        this._validateState();
      } else {
        // If there wasn't any state, redirect the user to the login screen.
        this.$location.url(this.authRoutesManager.login(false));
      }
    } else {
      // If the container didn't receive a token, redirect the user to the login screen.
      this.$location.url(this.authRoutesManager.login(false));
    }
  }
  /**
   * This is called by the accounts selector container if the user doesn't have a customer assigned.
   * It redirects the browser back to the login screen and shows the error.
   */
  emptyAccounts() {
    this.ssoErrors.noAccounts();
  }
  /**
   * This is the callback sent to the container that allows the user to select an account.
   * Gets the ID of the selected account and call the method that finishes the authentication
   * process.
   * @param {Object}  account The selected account.
   * @param {boolean} canSwitchAccount Whether or not the user will be able to switch the account.
   */
  selectAccount(account, canSwitchAccount) {
    this._success(account.photorank_id, canSwitchAccount);
  }
  /**
   * This is called by the accounts selector container if theuser wants to stop the authentication
   * process and signout instead.
   */
  signout() {
    this.loading = true;
    this.ssoClient.signoutAndRevoke()
    .catch((error) => {
      this.appErrorHandler.silent(error);
    })
    .then(() => {
      this.$timeout(() => {
        this.$location.url(this.authRoutesManager.login(true));
      });
    });
  }
  /**
   * Finishes the authentication process by calling the service that sends the token and, if
   * required, an account ID to the SSO client.
   * @param {number}  [accountId=0]            The Photorank ID of a selected account.
   * @param {boolean} [canSwitchAccount=false] Whether or not the user will be able to switch the
   *                                           account.
   * @access protected
   * @ignore
   */
  _success(accountId = 0, canSwitchAccount = false) {
    this.ssoStorage.saveUserEmailAddress(this._user.email);
    this.ssoClient.success(
      this.token,
      this._sessionId,
      this._user.id,
      accountId,
      canSwitchAccount,
    );
  }
  /**
   * Validates whether the user has permissions for a selected application or not.
   * The application ID is obtained from the current `state` property, and the token is recevied
   * from the container bindings.
   * @access protected
   * @ignore
   */
  _validateApp() {
    // Get whether the user has at least one permission or not.
    this.auth.validateApplicationPermissions(this.token, this.state.application)
    .then((valid) => {
      // Save the result of the validation on the local property.
      this.validApp = valid;
      if (this.validApp && !this.state.accountSelection) {
        /**
         * If the validation was a success and the user doesn't need to select an account,
         * finish the process.
         */
        this._success();
      } else if (!this.validApp) {
        // If the validation didn't pass, redirect the user to the error screen.
        this.ssoErrors.invalidPermissions();
      }
    })
    .catch((error) => {
      /**
       * If something failed on the request, log the error and redirect the user to the error
       * screen.
       */
      this.appErrorHandler.silent(error);
      this.ssoErrors.invalidPermissions();
    })
    .then(() => {
      this.loading = false;
    });
  }
  /**
   * Loads the user information for the token in order to send the ID and use it on the cookie;
   * after that it validates the state properties so it can show or not the accounts selector.
   * @access protected
   * @ignore
   */
  _validateState() {
    this.loading = true;
    this.auth.createUserSession(this.token)
    .then((info) => {
      this._user = info.user_account;
      this._sessionId = info.sessionid;
      if (this.state.application && this.state.applicationPermissionsRequired) {
        // Validate the user permissions for the selected application.
        this.needsToValidateApp = true;
        this._validateApp();
      } else if (!this.state.accountSelection) {
        // No need to validate anything else, so finish the process.
        this._success();
      } else {
        /**
         * Since there's a possibility that the account could have been selected on
         * another tab, we'll check if there's information about it on the cookie
         * first, and if there is, we'll skip the selection and go straight to the
         * success handler.
         */
        const account = this.ssoClient.getAccountForClient();

        if (account) {
          this._success(
            account.accountId,
            account.canSwitchAccount,
          );
        } else {
          this.loading = false;
          /**
           * If the user needs to select an account, add a listener to redirect the user back to the
           * login in case the cookie is removed during the selection process.
           */
          this.ssoClient.onSessionInterrupted(() => {
            this.$timeout(() => {
              this.$location.url(this.authRoutesManager.login(true));
            });
          });
        }
      }
    })
    .catch((error) => {
      /**
       * If something failed on the request, log the error and redirect the user to the error
       * screen.
       */
      this.appErrorHandler.silent(error);
      this.ssoErrors.noUser();
    });
  }
}
/**
 * Once the authentication processed _"finishes"_, this container will then validate the user
 * information to decided whether or not it meets the requirements of the SSO client
 * implementation.
 * @ngesdoc component
 * @ngname loginSuccessContainer
 * @ngcontroller {@link SuccessContainer}
 * @ngcontainer
 * @ngmodule auth
 * @ngbinding {string} token The user authentication token, obtained after the first part of the
 *                           login process.
 */
export default {
  controller: SuccessContainer,
  template: `
    <div
      ng-if="$ctrl.loading"
      class="odsSmallIndicator -fullWidth -fullHeight"
    ></div>
    <account-selector-container
      ng-if="
        !$ctrl.loading &&
        $ctrl.state.accountSelection &&
        (!$ctrl.needsToValidateApp || $ctrl.validApp)"
      token="{{::$ctrl.token}}"
      permissions-scope="{{::$ctrl.state.accountScope}}"
      preferred-id="{{::$ctrl.state.accountId}}"
      on-selection="$ctrl.selectAccount(account, canSwitchAccount)"
      on-empty-accounts="$ctrl.emptyAccounts()"
      on-signout="$ctrl.signout()"
    ></account-selector-container>
  `,
  bindings: {
    token: '@',
  },
};
